// Per-questionnaire progress for the admin vendor detail page. Each
// questionnaire tab shows its own Progress bar with the SAME numbers the
// vendor's post-submit view shows ("All questions (N)" / "N complete"):
// every visible question counts toward the total, and a question is complete
// exactly when the vendor owes no action on it. The per-question decision is
// delegated to needsVendorAction — the single source of truth shared with the
// vendor nav badge, dashboard, and Needs-action tab — so the two portals can
// never drift. Kept pure (no React / no network) so it can be unit-tested
// directly, the same discipline as companyDetailTabs.ts and wizard.ts.

import { needsVendorAction } from './vendorActions.ts';
import { isAwaitingAdmin, type ReviewSubmission } from './reviewState.ts';
import type { CompletionReason } from './visibility.ts';
import type { QuestionnaireGroup } from './companyDetailTabs.ts';

export type QuestionnaireProgress = {
  /** Visible questions in this questionnaire, required or not. */
  total: number;
  /** Of those, how many need no further vendor action. */
  completed: number;
  /** Rounded percentage; 0 when there are no visible questions (matches the vendor bar). */
  percent: number;
};

/**
 * Count completed vs total visible questions for one questionnaire tab.
 *
 * Only the questionnaire's visible (per `visibleIds`) questions are counted —
 * the same population the vendor's post-submit list renders, including
 * optional, compliance-exempt and retired questions. Completion mirrors the
 * vendor exactly: sent-back (rejected) or admin-commented-last items and
 * unanswered required questions are outstanding; everything else (answered,
 * approved / N-A, or simply not required) is complete.
 */
export function questionnaireProgress(
  group: QuestionnaireGroup,
  visibleIds: Set<string>,
  subByQ: ReadonlyMap<string, ReviewSubmission>,
  completionByQuestion: Map<string, CompletionReason | null>,
): QuestionnaireProgress {
  let total = 0;
  let completed = 0;
  for (const section of group.sections) {
    for (const question of section.questions) {
      if (!visibleIds.has(question.id)) continue;
      total += 1;
      if (needsVendorAction(question, subByQ.get(question.id), completionByQuestion) === null) {
        completed += 1;
      }
    }
  }
  const percent = total === 0 ? 0 : Math.round((completed * 100) / total);
  return { total, completed, percent };
}

export type ReviewProgress = {
  /** Answered questions the admin has acted on (approved / N-A / sent back / commented last). */
  reviewed: number;
  /** Answered questions still awaiting an admin decision. */
  needReview: number;
  /** Rounded percentage of reviewable items already reviewed; 0 when nothing is reviewable. */
  percent: number;
};

/**
 * The admin's own review progress for one questionnaire tab ("N reviewed ·
 * M need review"). Only visible questions WITH a submission count — an
 * unanswered question has nothing to review yet. A question needs review
 * exactly when isAwaitingAdmin says the admin owes the next move, so this bar,
 * the "Action required" pill and the outstanding badges all agree.
 */
export function reviewProgress(
  group: QuestionnaireGroup,
  visibleIds: Set<string>,
  subByQ: ReadonlyMap<string, ReviewSubmission>,
): ReviewProgress {
  let reviewed = 0;
  let needReview = 0;
  for (const section of group.sections) {
    for (const question of section.questions) {
      if (!visibleIds.has(question.id)) continue;
      const sub = subByQ.get(question.id);
      if (!sub) continue;
      if (isAwaitingAdmin(sub)) needReview += 1;
      else reviewed += 1;
    }
  }
  const reviewable = reviewed + needReview;
  const percent = reviewable === 0 ? 0 : Math.round((reviewed * 100) / reviewable);
  return { reviewed, needReview, percent };
}
